import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ShopService } from 'src/app/services/shop/shop.service';
import { ComercioActivoService } from 'src/app/services/merchant/comercio-activo.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-shop-detail',
  templateUrl: './shop-detail.component.html',
  styleUrls: ['./shop-detail.component.css']
})
export class ShopDetailComponent implements OnInit {
  shopCode: string = '';
  shop: any = null;
  loading: boolean = true;
  isEditMode: boolean = false;
  isNewShop: boolean = false;

  // Form data
  formData = {
    code: '',
    name: '',
    description: '',
    short_name: '',
    address: '',
    register_clients: false,
    available_purchases: false,
    visibility: false,
    status: true
  };

  // Validation
  errors: any = {};

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private shopService: ShopService,
    private _comercioActivo: ComercioActivoService
  ) { }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.shopCode = params['code'];
      
      if (this.shopCode === 'new') {
        this.isNewShop = true;
        this.isEditMode = true;
        this.loading = false;
      } else {
        this.loadShopData();
      }
    });
  }

  /**
   * Cargar datos de la tienda
   */
  loadShopData = async () => {
    this.loading = true;
    try {
      const response = await this.shopService.getShops(this.shopCode);
      
      console.log('Response from getShops:', response);
      
      let shopData = null;
      
      // Manejar diferentes formatos de respuesta
      if (response) {
        if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          shopData = response.data[0];
        } else if (Array.isArray(response) && response.length > 0) {
          shopData = response[0];
        } else if (response.shop) {
          shopData = response.shop;
        } else if (response.code) {
          // La respuesta es directamente la tienda
          shopData = response;
        }
      }
      
      if (shopData) {
        this.shop = shopData;
        this.populateForm();
        console.log('Shop loaded:', this.shop);
      } else {
        console.error('Shop not found in response:', response);
        Swal.fire({
          icon: 'error',
          title: 'Tienda no encontrada',
          text: 'No se encontró la tienda solicitada',
          confirmButtonColor: '#01abce'
        });
        this.goBack();
      }
    } catch (error) {
      console.error('Error loading shop:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo cargar la información de la tienda',
        confirmButtonColor: '#01abce'
      });
      this.goBack();
    } finally {
      this.loading = false;
    }
  };

  /**
   * Poblar formulario con datos de la tienda
   */
  populateForm = () => {
    this.formData = {
      code: this.shop.code || '',
      name: this.shop.name || '',
      description: this.shop.description || '',
      short_name: this.shop.short_name || '',
      address: this.shop.address || '',
      register_clients: this.shop.register_clients || false,
      available_purchases: this.shop.available_purchases || false,
      visibility: this.shop.visibility || false,
      status: this.shop.status !== undefined ? this.shop.status : true
    };
  };

  /**
   * Activar modo edición
   */
  enableEditMode = () => {
    this.isEditMode = true;
  };

  /**
   * Cancelar edición
   */
  cancelEdit = () => {
    if (this.isNewShop) {
      this.goBack();
    } else {
      this.isEditMode = false;
      this.populateForm();
      this.errors = {};
    }
  };

  /**
   * Validar formulario
   */
  validateForm = (): boolean => {
    this.errors = {};
    let isValid = true;

    // Código (solo para nueva tienda)
    if (this.isNewShop) {
      if (!this.formData.code || this.formData.code.trim() === '') {
        this.errors.code = 'El código es obligatorio';
        isValid = false;
      }
    }

    // Nombre
    if (!this.formData.name || this.formData.name.trim() === '') {
      this.errors.name = 'El nombre es obligatorio';
      isValid = false;
    }

    // Nombre corto: obligatorio, hasta 30 caracteres (lo exige el núcleo)
    if (!this.formData.short_name || this.formData.short_name.trim() === '') {
      this.errors.short_name = 'El nombre corto es obligatorio';
      isValid = false;
    } else if (this.formData.short_name.trim().length > 30) {
      this.errors.short_name = 'El nombre corto es muy largo (hasta 30 caracteres)';
      isValid = false;
    }

    return isValid;
  };

  /**
   * Guardar tienda
   */
  saveShop = async () => {
    if (!this.validateForm()) {
      Swal.fire({
        icon: 'warning',
        title: 'Formulario incompleto',
        text: 'Por favor completa todos los campos obligatorios',
        confirmButtonColor: '#01abce'
      });
      return;
    }

    try {
      if (this.isNewShop) {
        // Crear nueva tienda
        // Con un comercio elegido arriba, la tienda nace de ese comercio (ver
        // shops-list): la API lo toma del cuerpo.
        const comercio = this._comercioActivo.codigo;
        const response = await this.shopService.createShop({
          ...this.formData,
          ...(comercio ? { merchant: comercio } : {})
        });
        
        if (response && response.success) {
          Swal.fire({
            icon: 'success',
            title: 'Tienda creada',
            text: 'La tienda se ha creado correctamente',
            confirmButtonColor: '#01abce',
            timer: 2000
          });
          
          // Redirigir a la lista
          this.router.navigate(['/console/shops']);
        } else {
          throw new Error(response?.message || 'Error al crear la tienda');
        }
      } else {
        // Actualizar tienda existente
        // No enviar el código en el body (está protegido)
        const updateData: any = { ...this.formData };
        delete updateData.code;
        
        const response = await this.shopService.updateShop(this.shopCode, updateData);
        
        if (response && response.success) {
          Swal.fire({
            icon: 'success',
            title: 'Tienda actualizada',
            text: 'La tienda se ha actualizado correctamente',
            confirmButtonColor: '#01abce',
            timer: 2000
          });
          
          this.isEditMode = false;
          await this.loadShopData();
        } else {
          throw new Error(response?.message || 'Error al actualizar la tienda');
        }
      }
    } catch (error: any) {
      console.error('Error saving shop:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'No se pudo guardar la tienda',
        confirmButtonColor: '#01abce'
      });
    }
  };

  /**
   * Cambiar estado de la tienda
   */
  toggleShopStatus = async () => {
    const currentStatus = this.shop.status;
    const actionText = currentStatus ? 'inactivar' : 'activar';
    
    const result = await Swal.fire({
      title: `¿${actionText.charAt(0).toUpperCase() + actionText.slice(1)} tienda?`,
      text: `¿Estás seguro de que deseas ${actionText} esta tienda?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#01abce',
      cancelButtonColor: '#6c757d',
      confirmButtonText: `Sí, ${actionText}`,
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        const response = await this.shopService.toggleShopStatus(this.shopCode);
        
        if (response && response.shop) {
          Swal.fire({
            icon: 'success',
            title: 'Éxito',
            text: response.msg || `Tienda ${response.newStatus ? 'activada' : 'inactivada'} correctamente`,
            confirmButtonColor: '#01abce',
            timer: 2000
          });

          await this.loadShopData();
        } else {
          throw new Error('Respuesta inválida del servidor');
        }
      } catch (error) {
        console.error('Error toggling shop status:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo cambiar el estado de la tienda',
          confirmButtonColor: '#01abce'
        });
      }
    }
  };

  /**
   * Volver a la lista
   */
  goBack = () => {
    this.router.navigate(['/console/shops']);
  };
}
